import { requestAxios } from 'services/api';
import {
  postCodeFactory,
  returnConsignmentFactory,
  returnDetailsFactory,
  returnEntriesFactory,
  returnHistoryFactory,
} from 'factories';
import {
  PostCode,
  ReturnConsignment,
  ReturnDetails,
  ReturnEntries,
  ReturnRequestAction,
} from 'types';

interface GetParams {
  startDate?: number;
  endDate?: number;
  returnStatuses?: string;
  storeNames?: string;
  codes?: string;
  protocols?: string;
  customerName?: string;
  cpf?: string;
}

async function get(params: GetParams): Promise<ReturnConsignment[]> {
  let result: ReturnConsignment[] = [];
  params.storeNames = params.storeNames || undefined;

  try {
    const response = await requestAxios.get('returnRequest', { params });

    result = response.data;
  } catch (error) {
    console.error(error);
  }

  return returnConsignmentFactory(result);
}

interface GetEntriesParams {
  consignmentCode: string;
}

async function getEntries({
  consignmentCode,
}: GetEntriesParams): Promise<ReturnEntries> {
  const response = await requestAxios.get(
    `consignments/${consignmentCode}/returnEntries`
  );

  const result = response?.data ?? {};

  return returnEntriesFactory(result);
}

interface GetDetailsParams {
  protocol: string;
}

async function getDetails({
  protocol,
}: GetDetailsParams): Promise<ReturnDetails> {
  const response = await requestAxios.get(`returnRequest/${protocol}/details`);

  const result = response?.data ?? {};

  return returnDetailsFactory(result);
}

interface PostReturnRequest {
  code: string;
  option: number | undefined;
  entries: {
    entry: number | undefined;
    reason: string | undefined;
    additionalReason?: string | undefined;
    additionalInformation?: string | undefined;
  }[];
}

async function postCreateReturn(
  params: PostReturnRequest
): Promise<ReturnDetails> {
  const response = await requestAxios.post('returnRequest', params);

  const result = response?.data ?? {};

  return returnDetailsFactory(result);
}

interface PostConfirmReceipt {
  protocol: string;
  action: ReturnRequestAction;
  description?: string;
}

async function postConfirmReceipt({
  protocol,
  action,
  description,
}: PostConfirmReceipt): Promise<boolean> {
  const body = {
    description: description || undefined,
  };

  const response = await requestAxios.post(
    `/returnRequest/${protocol}/confirm`,
    body,
    {
      params: {
        action,
      },
    }
  );

  const result = response?.status;

  return result === 200;
}

interface PostPostCode {
  protocol: string;
}

async function postPostCode({ protocol }: PostPostCode): Promise<PostCode> {
  const response = await requestAxios.post(
    `/returnRequest/${protocol}/postCode`
  );

  const result = response?.data || {};

  return postCodeFactory(result);
}

interface GetHistory {
  protocol: string;
}

async function getHistory({ protocol }: GetHistory) {
  const response = await requestAxios.get(`/returnRequest/${protocol}/history`);

  const result = response?.data?.data || [];

  return returnHistoryFactory(result);
}

export const exchangeAndReturnService = {
  get,
  getEntries,
  getDetails,
  getHistory,
  postCreateReturn,
  postConfirmReceipt,
  postPostCode,
};
